import {useEffect, useRef, useState} from "react";
import PlanDTO from "../../Models/DTOs/Plan";
import {GetSignupQuote, SignupQuote, SignupQuoteRequest, signupQuoteRequest} from "../../Api/SignupQuote";

interface Options {
    plan: PlanDTO | null;
    userId?: string | null;
    affiliateCode?: string | null;
    getValidAccessToken: () => Promise<string | null>;
    enabled?: boolean;
}

export function useSignupQuote({plan, userId, affiliateCode, getValidAccessToken, enabled = true}: Options) {
    const key = enabled && plan
        ? JSON.stringify(signupQuoteRequest(plan, userId, affiliateCode))
        : "";
    const currentKey = useRef(key);
    currentKey.current = key;
    const tokenGetter = useRef(getValidAccessToken);
    tokenGetter.current = getValidAccessToken;
    const [attempt, setAttempt] = useState(0);
    const [state, setState] = useState<{key: string; quote: SignupQuote | null; error: string}>({
        key: "", quote: null, error: "",
    });

    useEffect(() => {
        if (!key) return;
        const controller = new AbortController();
        let cancelled = false;
        setState({key, quote: null, error: ""});
        const request: SignupQuoteRequest = JSON.parse(key);
        (async () => {
            try {
                const token = request.user_id ? await tokenGetter.current() : undefined;
                const quote = await GetSignupQuote(request, controller, token);
                if (request.affiliate_code && !quote.affiliate_eligible) {
                    throw new Error("This affiliate link could not be confirmed. Please check the link or try again.");
                }
                if (!cancelled) setState({key, quote, error: ""});
            } catch (error) {
                if (!cancelled) setState({
                    key, quote: null,
                    error: error instanceof Error && !("isAxiosError" in error)
                        ? error.message : "Your price could not be confirmed. Please try again.",
                });
            }
        })();
        return () => {
            cancelled = true;
            controller.abort();
        };
    }, [key, attempt]);

    const current = state.key === key;
    return {
        quote: current && key ? state.quote : null,
        isCurrent: () => currentKey.current === key,
        loading: !!key && (!current || (!state.quote && !state.error)),
        error: current && key ? state.error : "",
        retry: () => {
            setState({key, quote: null, error: ""});
            setAttempt(value => value + 1);
        },
    };
}
